import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { Select } from "../ui/Select";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { honorsApi } from "../../api/honors";
import { useActivities } from "../../hooks/useActivities";

const today = new Date().toISOString().slice(0, 10);

const schema = z.object({
  activityId: z.coerce.number().min(1, "Select an activity"),
  year: z.coerce.number().min(2020).max(2100),
  earnedDate: z.string().min(1, "Date earned is required"),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  camperId: number;
  onSuccess: () => void;
}

export default function HonorForm({ camperId, onSuccess }: Props) {
  const { activities, loading } = useActivities();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      year: new Date().getFullYear(),
      earnedDate: today,
    },
  });

  const activityOptions = activities.map((act) => ({
    value: act.id,
    label: act.name,
  }));

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    try {
      await honorsApi.create({ ...data, camperId, level: "honor" });
      toast.success("Honor awarded!");
      onSuccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to award honor");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Select
        label="Activity"
        options={activityOptions}
        placeholder={loading ? "Loading…" : "Select an activity"}
        error={errors.activityId?.message}
        {...register("activityId")}
      />

      <Input
        label="Date Earned"
        type="date"
        error={errors.earnedDate?.message}
        {...register("earnedDate")}
      />

      <Input
        label="Session Year"
        type="number"
        error={errors.year?.message}
        {...register("year")}
      />

      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1.5">
          Notes (optional)
        </label>
        <textarea
          {...register("notes")}
          rows={3}
          placeholder="Reason for honor…"
          className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="submit" variant="primary" loading={submitting}>
          Award Honor
        </Button>
      </div>
    </form>
  );
}
